// src/data/governanceData.js

// High‑level governance “north star” for the AI Implementation Blueprint
export const GOVERNANCE_DATA = {
  principles: [
    'Transparency – stakeholders understand when, where, and how AI is used in the business.',
    'Accountability – named owners are responsible for AI‑assisted decisions and outcomes.',
    'Fairness – AI systems are designed and monitored to treat customers and employees equitably.',
    'Privacy & Security – customer and employee data is collected, stored, and used with clear safeguards.',
    'Human Oversight – humans remain in control of high‑impact, sensitive, or novel decisions.',
    'Business Value – every AI initiative is tied to a measurable business goal (revenue, savings, risk).',
  ],

  categories: [
    {
      name: 'Technical Risks',
      items: [
        'Data quality & integration issues (gaps, duplicates, missing context)',
        'System integration complexity and fragile “glue code” between tools',
        'AI output quality, hallucinations, and lack of explainability',
        'Model drift as data, products, or customer behavior change over time',
      ],
    },
    {
      name: 'Organizational Risks',
      items: [
        'Employee resistance, fear of replacement, or low trust in AI outputs',
        'Skill gaps for configuring, monitoring, and improving AI workflows',
        'Change‑management fatigue from “yet another tool” or initiative',
        'Unclear ownership for AI projects across departments',
      ],
    },
    {
      name: 'Business & Vendor Risks',
      items: [
        'Vendor lock‑in and difficulty switching tools later',
        'ROI shortfall vs. expectations set during pilot phase',
        'Hidden costs (overages, token usage, implementation services)',
        'Misalignment between vendor roadmap and your strategy',
      ],
    },
    {
      name: 'Security, Privacy & Compliance',
      items: [
        'Use of production data in prompts without proper access controls',
        'Storage of chat history or logs in third‑party systems',
        'Regulatory compliance gaps (sector‑specific and regional)',
        'Bias, discrimination, or unfair treatment in automated decisions',
      ],
    },
  ],

  structure: {
    committee: 'AI Oversight Committee meeting monthly in Year 1 (quarterly after maturity).',
    champions:
      'One AI Champion per department (ops, marketing, finance, HR, etc.) responsible for tracking usage and outcomes.',
    decisionFramework: [
      {
        color: 'Green',
        description:
          'Full AI automation – repetitive, rules‑based tasks with low downside risk (drafting emails, summaries, data cleanup).',
      },
      {
        color: 'Yellow',
        description:
          'Human‑in‑the‑loop – customer‑facing or financially material decisions where AI drafts and humans approve.',
      },
      {
        color: 'Red',
        description:
          'No AI – highly sensitive, empathy‑heavy, or novel situations (disciplinary actions, critical legal decisions, layoffs).',
      },
    ],
    minimumControls: [
      'Access controls: who can use which AI tools, and with which data.',
      'Review cadence: how often you review prompts, flows, and logs.',
      'Incident path: simple way for staff to flag AI mistakes or risks.',
      'Sunset criteria: when to turn off or redesign an AI workflow.',
    ],
  },
};

// Industry‑specific warnings / flags surfaced in the Governance tab
export const INDUSTRY_WARNINGS = {
  healthcare:
    'HIPAA (or local equivalent) applies to any workflow touching protected health information. Avoid pasting identifiable patient data into third‑party tools without a BAA.',
  finance:
    'SOC 2 / PCI‑DSS and similar frameworks apply to AI‑assisted transactions. Treat account numbers, cards, and PII as highly sensitive and keep them out of consumer chat tools.',
  education:
    'FERPA restrictions apply to student records and performance data. Do not expose identifiable student information to tools that are not FERPA‑aligned.',
  ecommerce:
    'Customer contact details and order history are sensitive. Limit who can use real customer data in AI workflows and prefer anonymized datasets for experimentation.',
  retail:
    'Customer PII (names, emails, purchase history) used in AI tools must comply with CCPA/GDPR where applicable. Ensure loyalty program data is handled with explicit consent.',
  hospitality:
    'Guest data — reservation details, preferences, payment info — is high‑value and high‑risk. PCI‑DSS applies to any AI tool that touches card data or booking payments.',
  real_estate:
    'Fair Housing Act restrictions apply. Do not use AI to filter, target, or exclude buyers/renters based on protected characteristics. Document all lead scoring decisions.',
  // R42 (Sarah R36g verdict): PM operators don't have "buyers" — they
  // have applicants, tenants, and owners. FHA still applies (advertising,
  // tenant-selection criteria) but the surface text and the lookup key
  // both need to read PM-correct.
  property_management:
    'Fair Housing Act restrictions apply to tenant screening, listing copy, and applicant communications. Do not use AI to filter, target, or exclude applicants based on protected characteristics. FCRA permissible-purpose + §615 adverse-action timing apply to any AI-assisted screening workflow. Document all applicant decisions.',
  professional_services:
    "Client confidentiality matters. If you're in a regulated profession (legal, accounting, credit repair, financial advisory), check your licensing body's rules before pasting client data into consumer AI tools. General rule: treat client PII as confidential and prefer AI tools with enterprise data-handling agreements.",
  technology:
    "IP and trade‑secret risk: code, internal architecture, and product roadmaps pasted into AI assistants may be used for model training. Review your AI vendor's data usage policy.",
  manufacturing:
    'Proprietary process data and supplier contracts are sensitive. Limit AI access to operational data to named users with role‑based controls.',
  hr:
    'Hiring, performance reviews, and promotions can trigger anti‑discrimination obligations. Do not fully automate people decisions; always document human review.',
};

// Tool category → brief compliance/privacy note for the Governance tab
export const TOOL_COMPLIANCE_NOTES = {
  crm: 'Contains customer PII — enforce role‑based access and avoid exporting contact lists to general AI tools.',
  email: 'Email sequences may contain personal data — review opt‑out compliance (CAN‑SPAM/GDPR).',
  payments: 'PCI‑DSS scope — never paste card numbers or transaction IDs into AI chat tools.',
  analytics: 'Ensure tracking pixels comply with cookie consent laws in every market you serve.',
  ai_assistant: 'Do not paste customer PII, financial records, or trade secrets into general‑purpose AI tools.',
  automation: 'Audit automation flows for personally identifiable data traversing third‑party systems.',
  accounting: 'Financial PII — restrict AI access to anonymized reports; keep raw ledger data off chat tools.',
  hr: 'Employee data is sensitive — apply strict access controls and document AI usage in all HR decisions.',
  marketing: 'Audience targeting data must comply with CCPA/GDPR consent requirements.',
  healthcare_tech: 'Any PHI passing through this tool requires a signed BAA with the vendor before use.',
};
